// src/pages/TokenExchangePage.jsx — Auth Hub SSO callback handler.
//
// Receives the hub's custom token (URL fragment), exchanges it for a hub
// Firebase session, then redirects to the stored return path. Modelled on
// finding-your-voice's TokenExchange page.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authHubService } from '../services/authHubService';

const TokenExchangePage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const performExchange = async () => {
    // The service forgets the hub tokens after one attempt, success or failure,
    // so a retry goes back through the hub for fresh ones.
    if (!authHubService.hasAuthToken()) {
      authHubService.redirectToLogin(authHubService.getRedirectPath());
      return;
    }
    try {
      setError(null);
      setRetrying(true);

      await authHubService.handleTokenExchange();
      const redirectPath = authHubService.getRedirectPath();
      navigate(redirectPath, { replace: true });
    } catch (err) {
      console.error('Token exchange failed:', err);
      setError(err.message || 'Authentication failed. Please try signing in again.');
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    if (authHubService.hasAuthToken()) {
      performExchange();
    } else {
      setError('No authentication token found. Please try signing in again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-black px-4">
      <div className="text-center max-w-md w-full">
        {error ? (
          <div className="bg-charcoal/80 border border-pearl/10 rounded-2xl p-8" role="alert">
            <h1 className="text-xl font-bold text-pearl mb-3">Sign-in problem</h1>
            <p className="text-sm text-pearl/70 mb-6">{error}</p>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={performExchange}
                disabled={retrying}
                className="px-6 py-3 rounded-xl bg-conversation-teal text-gunmetal font-semibold disabled:opacity-60"
              >
                {retrying ? 'Retrying…' : 'Try again'}
              </button>
              <button
                type="button"
                onClick={() => authHubService.redirectToLogin('/dashboard')}
                className="px-6 py-3 rounded-xl border border-pearl/20 text-pearl/80"
              >
                Back to sign in
              </button>
            </div>
          </div>
        ) : (
          <div>
            <img
              src="/profound-philosophy-logo.webp"
              alt="Profound Philosophy"
              className="w-24 h-24 mx-auto mb-4 animate-pulse"
            />
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-pearl/80" role="status">Completing sign in…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TokenExchangePage;
